/**
 * @fileoverview Connect banking-submission records to staff inspection, client matching, and explicit lookup retries. Backend submission identity and access govern accepted assignments. Local filtering and detail selection remain presentation; assigning a client or retrying a lookup is separate from verifying banking details or moving funds.
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AppButton, AppTag, AppModal, AppDropdown, AppTextInput, AppSpinner, AppCalendar, AppAutoComplete } from '../shared';
import api from '../../api';
import ToastContext from '../../ToastContext';

// The banking submission pipeline stages shown in the workflow rail
import AppIcon from "../shared/AppIcon";const STEPS = [
{ key: 'submitted', label: 'Submitted' },
{ key: 'security_code', label: 'Security Code' },
{ key: 'client_lookup', label: 'Client Lookup' },
{ key: 'verification', label: 'Verification' }];




function getStepStates(submission) {
  const { status, security_code_valid } = submission;

  // Each step: 'complete' | 'active' | 'error' | 'pending'
  const steps = {};

  // Step 1: Submitted — always complete if we have a record
  steps.submitted = 'complete';

  // Step 2: Security Code
  if (!security_code_valid) {
    steps.security_code = 'error';
    steps.client_lookup = 'pending';
    steps.verification = 'pending';
    return steps;
  }
  steps.security_code = 'complete';

  // Step 3: Client Lookup
  if (status === 'pending_client_lookup') {
    steps.client_lookup = 'active';
    steps.verification = 'pending';
    return steps;
  }
  if (status === 'timeout_client_not_found') {
    steps.client_lookup = 'error';
    steps.verification = 'pending';
    return steps;
  }
  // Client was found (has client_found_at or progressed past lookup)
  steps.client_lookup = 'complete';

  // Step 4: Verification
  if (status === 'pending_verification') {
    steps.verification = 'active';
    return steps;
  }
  if (status === 'timeout_verification') {
    steps.verification = 'error';
    return steps;
  }
  steps.verification = status === 'verified' ? 'complete' : 'pending';

  return steps;
}

const stepColors = {
  complete: { bg: '#16a34a', border: '#15803d', text: '#ffffff', connector: '#16a34a' },
  active: { bg: '#3b82f6', border: '#2563eb', text: '#ffffff', connector: '#d1d5db' },
  error: { bg: '#dc2626', border: '#b91c1c', text: '#ffffff', connector: '#d1d5db' },
  pending: { bg: '#e5e7eb', border: '#d1d5db', text: '#9ca3af', connector: '#e5e7eb' }
};

const stepIcons = {
  complete: 'pi pi-check',
  active: 'pi pi-spin pi-spinner',
  error: 'pi pi-times',
  pending: ''
};



function ProgressBar({ submission, compact = false }) {
  const stepStates = getStepStates(submission);
  const circleSize = compact ? 28 : 32;
  const labelFontSize = compact ? '0.66rem' : '0.7rem';
  const labelMarginTop = compact ? '0.35rem' : '4px';
  const connectorHeight = compact ? '2px' : '3px';
  const connectorOffset = compact ? '-10px' : '-12px';
  const connectorMargin = compact ? '8px' : '4px';
  const minWidth = compact ? '68px' : '56px';
  const iconSize = compact ? '0.72rem' : '0.8rem';
  const stepPadding = compact ? '0.15rem 0 0' : '0.5rem 0';

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', padding: stepPadding }}>
            {STEPS.map(

            (step, i) => {
        const state = stepStates[step.key];
        const colors = stepColors[state];
        const icon = stepIcons[state];
        const isLast = i === STEPS.length - 1;

        return (
          <div key={step.key} style={{ display: 'flex', alignItems: 'center', flex: isLast ? '0 0 auto' : '1 1 0' }}>
                        {/* Step circle + label */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth }}>
                            <div style={{
                width: `${circleSize}px`,
                height: `${circleSize}px`,
                borderRadius: '50%',
                backgroundColor: colors.bg,
                border: `2px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.text,
                fontSize: iconSize,
                flexShrink: 0,
                transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease'
              }}>
                                {icon ? <AppIcon icon={icon} /> : <span style={{ fontSize: compact ? '0.68rem' : '0.7rem', fontWeight: 700 }}>{i + 1}</span>}
                            </div>
                            <span style={{
                fontSize: labelFontSize,
                fontWeight: 600,
                marginTop: labelMarginTop,
                color: state === 'pending' ? '#9ca3af' : state === 'error' ? '#dc2626' : 'var(--text-color)',
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}>
                                {step.label}
                            </span>
                        </div>

                        {/* Connector line */}
                        {!isLast &&
            <div style={{
              flex: 1,
              height: connectorHeight,
              backgroundColor: colors.connector,
              marginTop: connectorOffset,
              marginLeft: connectorMargin,
              marginRight: connectorMargin,
              borderRadius: '2px',
              transition: 'background-color 0.3s ease'
            }} />}

                    </div>);

      })}
        </div>);

}



function getStatusSubtext(submission) {
  const { status, client_lookup_attempts, verification_attempts } = submission;
  switch (status) {
    case 'error':
      return 'Security code failed validation. Review the submission before retrying.';
    case 'pending_client_lookup':
      return `Client search still running (${client_lookup_attempts} of 72 hourly checks).`;
    case 'timeout_client_not_found':
      return 'No client match was found after 72 hourly checks. Assign a client or review manually.';
    case 'pending_verification':
      return `Trello verification is still open (${verification_attempts} of 144 hourly checks).`;
    case 'timeout_verification':
      return 'Verification did not complete within 6 days. Review the Trello card and follow up.';
    default:
      return '';
  }
}



function getStatusSeverity(status) {
  switch (status) {
    case 'pending_client_lookup':return 'warning';
    case 'pending_verification':return 'info';
    case 'verified':return 'success';
    case 'timeout_client_not_found':
    case 'timeout_verification':
    case 'error':return 'danger';
    default:return null;
  }
}

const statusOptions = [
{ label: 'All', value: 'all' },
{ label: 'Pending Client Lookup', value: 'pending_client_lookup' },
{ label: 'Pending Verification', value: 'pending_verification' },
{ label: 'Verified', value: 'verified' },
{ label: 'Timeout - Client Not Found', value: 'timeout_client_not_found' },
{ label: 'Timeout - Verification', value: 'timeout_verification' },
{ label: 'Error', value: 'error' }];




function getStatusLabel(status) {
  const option = statusOptions.find((opt) => opt.value === status);
  return option ? option.label : status;
}



function getCompactStatusLabel(status) {
  switch (status) {
    case 'pending_client_lookup':
      return 'Pending Lookup';
    case 'pending_verification':
      return 'Pending Verification';
    case 'verified':
      return 'Verified';
    case 'timeout_client_not_found':
      return 'Client Not Found';
    case 'timeout_verification':
      return 'Verification Timed Out';
    case 'error':
      return 'Security Code Error';
    default:
      return getStatusLabel(status);
  }
}



function getStatusTone(status) {
  switch (status) {
    case 'pending_client_lookup':
    case 'pending_verification':
      return 'info';
    case 'verified':
      return 'success';
    case 'timeout_client_not_found':
    case 'timeout_verification':
    case 'error':
      return 'danger';
    default:
      return 'neutral';
  }
}

const timestampFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
});



function formatTimestamp(value) {
  if (!value) {
    return 'Unknown';
  }
  return timestampFormatter.format(new Date(value));
}



function formatDisplayValue(value) {
  if (!value) {
    return 'Unknown';
  }

  return String(value)
  .split('_')
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
  .join(' ');
}



function hasSameDisplayedTimestamp(left, right) {
  if (!left || !right) {
    return false;
  }

  return formatTimestamp(left) === formatTimestamp(right);
}



function getTimelineItems(submission) {
  return [
  { label: 'Submitted', value: formatTimestamp(submission.created_at) },
  submission.client_found_at && !hasSameDisplayedTimestamp(submission.client_found_at, submission.created_at) ?
  { label: 'Client Matched', value: formatTimestamp(submission.client_found_at) } :
  null,
  submission.verification_completed_at ? { label: 'Verified', value: formatTimestamp(submission.verification_completed_at) } : null]
  .filter(Boolean);
}



function SubmissionCard({ submission, onViewDetails, onRetryLookup, onAssignClient }) {
  const metaItems = [
  { icon: 'pi pi-envelope', label: 'Email', value: submission.email },
  { icon: 'pi pi-credit-card', label: 'Method', value: formatDisplayValue(submission.payment_method) },
  submission.bank_name ? { icon: 'pi pi-building', label: 'Bank', value: submission.bank_name } : null,
  submission.client_name ? { icon: 'pi pi-user', label: 'Client', value: submission.client_name } : null,
  submission.attorney_name ? { icon: 'pi pi-briefcase', label: 'Owner', value: submission.attorney_name } : null]
  .filter(Boolean);
  const statusSubtext = getStatusSubtext(submission);
  const timelineItems = getTimelineItems(submission);
  const openDetails = useCallback(

  () => {
    onViewDetails(submission);
  }, [onViewDetails, submission]);
  const handleCardKeyDown = useCallback(

  (event) => {
    if (event.currentTarget !== event.target) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      openDetails();
    }
  }, [openDetails]);
  const stopPropagation =

  (callback) => (event) => {
    event.stopPropagation();
    callback();
  };
  const showLookupActions = submission.status === 'pending_client_lookup' || submission.status === 'timeout_client_not_found';

  return (
    <article
      className="banking-submissions__card"
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${submission.full_name}`}
      onClick={openDetails}
      onKeyDown={handleCardKeyDown}>

            <div className="banking-submissions__card-header">
                <div className="banking-submissions__card-title-row">
                    <h3 className="banking-submissions__card-title">{submission.full_name}</h3>
                </div>

                <div className="banking-submissions__card-toolbar">
                    <div className="banking-submissions__card-toolbar-status">
                        <span className="banking-submissions__card-toolbar-label">Status</span>
                        <AppTag
              value={getCompactStatusLabel(submission.status)}
              severity={getStatusSeverity(submission.status)}
              className="banking-submissions__status-tag"
              rounded />

                    </div>
                    {submission.trello_card_url &&
          <AppButton
            label="Open Trello"
            icon="pi pi-external-link"
            variant="secondary"
            onClick={stopPropagation(() => window.open(submission.trello_card_url, '_blank'))} />}


                </div>
            </div>

            <div className="banking-submissions__meta-list">
                {metaItems.map((item) =>
        <div key={`${submission.id}-${item.label}-${item.value}`} className="banking-submissions__meta-pill">
                        <AppIcon icon={item.icon} aria-hidden="true" />
                        <div className="banking-submissions__meta-copy">
                            <span className="banking-submissions__meta-label">{item.label}</span>
                            <span className="banking-submissions__meta-value">{item.value}</span>
                        </div>
                    </div>
        )}
                {timelineItems.map((item) =>
        <div key={`${submission.id}-${item.label}`} className="banking-submissions__timeline-item">
                        <span className="banking-submissions__timeline-label">{item.label}</span>
                        <span className="banking-submissions__timeline-value">{item.value}</span>
                    </div>
        )}
            </div>

            {statusSubtext ?
      <div className={`banking-submissions__status-note is-${getStatusTone(submission.status)}`}>
                    {statusSubtext}
                </div> :
      null}

            <div className="banking-submissions__workflow">
                <div className="banking-submissions__section-label">Workflow</div>
                <ProgressBar submission={submission} compact />
            </div>

            {showLookupActions &&
      <div className="banking-submissions__card-actions">
                    <AppButton
          label="Retry Lookup"
          icon="pi pi-refresh"
          variant="secondary"
          onClick={stopPropagation(() => onRetryLookup(submission.id))} />

                    <AppButton
          label="Assign Client"
          icon="pi pi-user-plus"
          variant="secondary"
          onClick={stopPropagation(() => onAssignClient(submission))} />

                </div>}

        </article>);

}



function BankingSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailDialogVisible, setDetailDialogVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [assignDialogVisible, setAssignDialogVisible] = useState(false);
  const [assignSubmission, setAssignSubmission] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const showToast = useContext(ToastContext);

  const fetchSubmissions = useCallback(

  async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const response = await api.get('/banking/submissions', { params });
      setSubmissions(response.data.submissions || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load banking submissions', life: 3000 });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, startDate, endDate, showToast]);

  useEffect(() => {fetchSubmissions();}, [fetchSubmissions]);

  useEffect(() => {
    const hasPending = submissions.some((s) =>
    s.status === 'pending_client_lookup' || s.status === 'pending_verification'
    );
    if (hasPending) {
      const interval = setInterval(fetchSubmissions, 30000);
      return () => clearInterval(interval);
    }
  }, [submissions, fetchSubmissions]);

  const viewDetails =

  async (submission) => {
    try {
      const response = await api.get(`/banking/submissions/${submission.id}`);
      setSelectedSubmission(response.data);
      setDetailDialogVisible(true);
    } catch (error) {
      console.error('Error fetching submission details:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load submission details', life: 3000 });
    }
  };

  const retryClientLookup =

  async (submissionId) => {
    try {
      const response = await api.post(`/banking/submissions/${submissionId}/retry_client_lookup`);
      if (response.data.success) {
        showToast({ severity: 'success', summary: 'Success', detail: response.data.message, life: 3000 });
        fetchSubmissions();
      } else {
        showToast({ severity: 'info', summary: 'Info', detail: response.data.message, life: 3000 });
      }
    } catch (error) {
      console.error('Error retrying client lookup:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to retry client lookup', life: 3000 });
    }
  };

  const openAssignDialog =

  (submission) => {
    setAssignSubmission(submission);
    setSelectedClient(null);
    setClientSuggestions([]);
    setAssignDialogVisible(true);
  };

  const searchClients =

  async ({ query }) => {
    if (!query || query.length < 2) {
      setClientSuggestions([]);
      return;
    }
    try {
      const response = await api.get('/banking/clients/search', { params: { q: query } });
      setClientSuggestions(response.data || []);
    } catch (error) {
      console.error('Error searching clients:', error);
    }
  };

  const confirmAssignClient =

  async () => {
    if (!selectedClient || !assignSubmission) return;
    setAssigning(true);
    try {
      const response = await api.post(`/banking/submissions/${assignSubmission.id}/assign_client`, {
        client_id: selectedClient.id
      });
      if (response.data.success) {
        showToast({ severity: 'success', summary: 'Success', detail: response.data.message, life: 3000 });
        setAssignDialogVisible(false);
        fetchSubmissions();
      }
    } catch (error) {
      const detail = error.response?.data?.error || 'Failed to assign client';
      showToast({ severity: 'error', summary: 'Error', detail, life: 3000 });
    } finally {
      setAssigning(false);
    }
  };

  const renderAssignDialog =

  () =>
  <AppModal
    title="Assign Client"
    visible={assignDialogVisible}
    width="500px"
    onClose={() => setAssignDialogVisible(false)}>

            <div className="mb-3">
                <p className="mt-0" style={{ color: 'var(--text-color-secondary)' }}>
                    Search for a client to assign to <strong>{assignSubmission?.full_name}</strong>
                </p>
                <label className="block mb-2 font-semibold">Client</label>
                <AppAutoComplete
        value={selectedClient ? [selectedClient] : []}
        suggestions={clientSuggestions}
        completeMethod={searchClients}
        onChange={(e) => {
          if (Array.isArray(e.value)) {
            setSelectedClient(e.value[e.value.length - 1] || null);
          } else {
            setSelectedClient(e.value);
          }
        }}
        field="name"
        placeholder="Type client name..." />

            </div>
            {selectedClient &&
    <div className="surface-card p-3 border-round shadow-1 mb-3">
                    <div className="text-sm">
                        <strong>Selected:</strong> {selectedClient.name}<br />
                        <strong>Email:</strong> {selectedClient.email || 'N/A'}
                    </div>
                </div>}

            <div className="flex justify-content-end gap-2">
                <AppButton label="Cancel" variant="secondary" onClick={() => setAssignDialogVisible(false)} />
                <AppButton
        label={assigning ? 'Assigning...' : 'Assign Client'}
        icon={assigning ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
        onClick={confirmAssignClient}
        disabled={!selectedClient || assigning} />

            </div>
        </AppModal>;


  const renderDetailDialog =

  () => {
    if (!selectedSubmission) return null;
    const notifications = selectedSubmission.notifications || [];

    return (
      <AppModal
        title="Banking Submission Details"
        visible={detailDialogVisible}
        width="800px"
        onClose={() => setDetailDialogVisible(false)}>

                <div className="grid">
                    {/* Progress overview at top of detail */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-2" style={{ color: 'var(--text-color)' }}>Pipeline Progress</h3>
                            <ProgressBar submission={selectedSubmission} />
                            {getStatusSubtext(selectedSubmission) ?
              <div className="text-sm mt-1" style={{ color: 'var(--text-color-secondary)', fontStyle: 'italic' }}>
                                    {getStatusSubtext(selectedSubmission)}
                                </div> :
              null}
                        </div>
                    </div>

                    {/* Client Information */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Client Information</h3>
                            <div className="grid">
                                <div className="col-6">
                                    <strong>Name:</strong> {selectedSubmission.first_name} {selectedSubmission.last_name}
                                </div>
                                <div className="col-6">
                                    <strong>Email:</strong> {selectedSubmission.email}
                                </div>
                                <div className="col-6">
                                    <strong>Client:</strong> {selectedSubmission.client_name || 'Not found'}
                                </div>
                                <div className="col-6">
                                    <strong>Attorney:</strong> {selectedSubmission.attorney_name || 'N/A'}
                                </div>
                                <div className="col-6">
                                    <strong>Jurisdiction:</strong> {selectedSubmission.jurisdiction || 'N/A'}
                                </div>
                                <div className="col-6">
                                    <strong>Matter:</strong> {selectedSubmission.matter_number || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Banking Details */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Banking Details</h3>
                            <div className="grid">
                                <div className="col-6">
                                    <strong>Payment Method:</strong> {formatDisplayValue(selectedSubmission.payment_method)}
                                </div>
                                <div className="col-6">
                                    <strong>Bank:</strong> {selectedSubmission.bank_name || 'See attachment'}
                                </div>
                                <div className="col-6">
                                    <strong>Account Type:</strong> {selectedSubmission.account_type || 'See attachment'}
                                </div>
                                <div className="col-6">
                                    <strong>Account Number:</strong> {selectedSubmission.account_number || 'See attachment'}
                                </div>
                                <div className="col-12">
                                    <strong>Routing Number:</strong> {selectedSubmission.routing_number || 'See attachment'}
                                </div>
                                {selectedSubmission.file_url &&
                <div className="col-12">
                                        <AppButton
                    label="View Uploaded File"
                    icon="pi pi-external-link"
                    onClick={() => window.open(selectedSubmission.file_url, '_blank')} />

                                    </div>}

                            </div>
                        </div>
                    </div>

                    {/* Status Information */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Status Information</h3>
                            <div className="grid">
                                <div className="col-6">
                                    <strong>Status:</strong> <AppTag value={getStatusLabel(selectedSubmission.status)} severity={getStatusSeverity(selectedSubmission.status)} />
                                </div>
                                <div className="col-6">
                                    <strong>Security Code Valid:</strong> {selectedSubmission.security_code_valid ? 'Yes' : 'No'}
                                </div>
                                <div className="col-6">
                                    <strong>Client Lookup Attempts:</strong> {selectedSubmission.client_lookup_attempts}
                                </div>
                                <div className="col-6">
                                    <strong>Verification Attempts:</strong> {selectedSubmission.verification_attempts}
                                </div>
                                <div className="col-6">
                                    <strong>Created:</strong> {formatTimestamp(selectedSubmission.created_at)}
                                </div>
                                <div className="col-6">
                                    <strong>Last Updated:</strong> {formatTimestamp(selectedSubmission.updated_at)}
                                </div>
                                {selectedSubmission.client_found_at &&
                <div className="col-6">
                                        <strong>Client Found:</strong> {formatTimestamp(selectedSubmission.client_found_at)}
                                    </div>}

                                {selectedSubmission.verification_completed_at &&
                <div className="col-6">
                                        <strong>Verified:</strong> {formatTimestamp(selectedSubmission.verification_completed_at)}
                                    </div>}

                                {selectedSubmission.trello_card_url &&
                <div className="col-12">
                                        <AppButton
                    label="View Trello Card"
                    icon="pi pi-external-link"
                    onClick={() => window.open(selectedSubmission.trello_card_url, '_blank')} />

                                    </div>}

                            </div>
                        </div>
                    </div>

                    {/* Notifications */}
                    {notifications.length > 0 &&
          <div className="col-12">
                            <div className="surface-card p-3 border-round shadow-1">
                                <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Notification History</h3>
                                <div style={{ position: 'relative', paddingLeft: '1.5rem' }}>
                                    {notifications.map((item, i) =>
                <div key={i} style={{
                  position: 'relative',
                  paddingBottom: i < notifications.length - 1 ? '1.5rem' : 0,
                  borderLeft: i < notifications.length - 1 ? '2px solid var(--surface-border)' : 'none',
                  paddingLeft: '1.25rem'
                }}>
                                            <div style={{
                    position: 'absolute', left: '-0.45rem', top: '0.25rem',
                    width: '0.7rem', height: '0.7rem', borderRadius: '50%',
                    backgroundColor: 'var(--primary-color)'
                  }} />
                                            <div className="font-semibold">{item.notification_type}</div>
                                            <div className="text-sm text-600">To: {item.notification_target}</div>
                                            <div className="text-sm text-600">{formatTimestamp(item.sent_at)}</div>
                                        </div>
                )}
                                </div>
                            </div>
                        </div>}

                </div>
            </AppModal>);

  };

  // Sort: active/pending first, then by date
  const sortedSubmissions = [...submissions].sort(

  (a, b) => {
    const activeStatuses = ['pending_client_lookup', 'pending_verification'];
    const aActive = activeStatuses.includes(a.status) ? 0 : 1;
    const bActive = activeStatuses.includes(b.status) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const pendingCount = submissions.filter((s) => s.status === 'pending_client_lookup' || s.status === 'pending_verification').length;
  const verifiedCount = submissions.filter((s) => s.status === 'verified').length;
  const exceptionCount = submissions.filter((s) => s.status === 'timeout_client_not_found' || s.status === 'timeout_verification' || s.status === 'error').length;

  return (
    <div className="banking-submissions">
            <section className="banking-submissions__controls">
                <div className="banking-submissions__controls-header">
                    <div>
                        <div className="banking-submissions__controls-eyebrow">Submission Controls</div>
                        <div className="banking-submissions__controls-title">Filter the queue</div>
                    </div>
                    <div className="banking-submissions__summary-grid">
                        <div className="banking-submissions__summary-chip">
                            <span className="banking-submissions__summary-label">Visible</span>
                            <span className="banking-submissions__summary-value">{submissions.length}</span>
                        </div>
                        <div className="banking-submissions__summary-chip">
                            <span className="banking-submissions__summary-label">Pending</span>
                            <span className="banking-submissions__summary-value">{pendingCount}</span>
                        </div>
                        <div className="banking-submissions__summary-chip">
                            <span className="banking-submissions__summary-label">Verified</span>
                            <span className="banking-submissions__summary-value">{verifiedCount}</span>
                        </div>
                        <div className="banking-submissions__summary-chip">
                            <span className="banking-submissions__summary-label">Exceptions</span>
                            <span className="banking-submissions__summary-value">{exceptionCount}</span>
                        </div>
                    </div>
                </div>

                <div className="grid banking-submissions__filters-grid">
                    <div className="col-12 md:col-3 banking-submissions__filter-group">
                        <label htmlFor="status-filter" className="banking-submissions__filter-label">Status</label>
                        <AppDropdown
              id="status-filter"
              value={statusFilter}
              options={statusOptions}
              onChange={(e) => setStatusFilter(e.value)} />

                    </div>
                    <div className="col-12 md:col-3 banking-submissions__filter-group">
                        <label htmlFor="search" className="banking-submissions__filter-label">Search (Name/Email)</label>
                        <AppTextInput
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..." />

                    </div>
                    <div className="col-12 md:col-2 banking-submissions__filter-group">
                        <label className="banking-submissions__filter-label">Start Date</label>
                        <AppCalendar
              value={startDate}
              onChange={(e) => setStartDate(e.value)}
              className="banking-submissions__date-picker" />

                    </div>
                    <div className="col-12 md:col-2 banking-submissions__filter-group">
                        <label className="banking-submissions__filter-label">End Date</label>
                        <AppCalendar
              value={endDate}
              onChange={(e) => setEndDate(e.value)}
              className="banking-submissions__date-picker" />

                    </div>
                    <div className="col-12 md:col-2 banking-submissions__filter-group banking-submissions__filter-group--action">
                        <AppButton
              label="Refresh"
              icon="pi pi-refresh"
              onClick={fetchSubmissions} />

                    </div>
                </div>
                <div className="banking-submissions__refresh-note">
                    {submissions.length} submission{submissions.length !== 1 ? 's' : ''} · Last refreshed: {lastRefresh.toLocaleTimeString()}
                    {submissions.some((s) => s.status === 'pending_client_lookup' || s.status === 'pending_verification') &&
          ' · Auto-refreshing every 30 seconds'}

                </div>
            </section>

            {loading && submissions.length === 0 ?
      <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <AppSpinner />
                </div> :
      sortedSubmissions.length === 0 ?
      <div className="surface-card p-4 border-round text-center" style={{ color: 'var(--text-color-secondary)' }}>
                    No banking submissions found
                </div> :

      sortedSubmissions.map((submission) =>
      <SubmissionCard
        key={submission.id}
        submission={submission}
        onViewDetails={viewDetails}
        onRetryLookup={retryClientLookup}
        onAssignClient={openAssignDialog} />

      )}


            {/* Detail Dialog */}
            {renderDetailDialog()}

            {/* Assign Client Dialog */}
            {renderAssignDialog()}
        </div>);

}

export default BankingSubmissions;
